import React, { useState } from "react";

export const useToggle = (
    initial: boolean = false
): [boolean, () => void, React.Dispatch<React.SetStateAction<boolean>>] => {
    const [flag, setFlag] = useState(initial);
    const toggleFlag = () => setFlag(!flag);
    return [flag, toggleFlag, setFlag];
};
export const useBoolean = (
    initial: boolean = false
): [boolean, () => void, () => void, React.Dispatch<React.SetStateAction<boolean>>] => {
    const [flag, setFlag] = useState(initial);
    const setTrue = () => setFlag(true);
    const setFalse = () => setFlag(false);
    return [flag, setTrue, setFalse, setFlag];
};

/**
 * MOVEit Transfer uses underscores to separate sublanguages,
 * whereas react-intl requires dashes. Also, all language
 * codes should be lowercase.
 * @param value - the locale value given by the Transfer WebApp
 */
export function getLanguageCode(value: string): string {
    let output = value || "en";
    output = output.replace("_", "-").toLowerCase();
    return output;
}

/**
 * A utility that returns a getter function for the latest value/ref to the given object (shallow comparison).
 * (Useful such as when an argument is a prop value and you want an async function to always use the most up-to-date value regardless of invocation time.)
 * @param obj the object whose latest reference you always want returned by the returned getter function
 * @returns a getter function that always returns the latest value/ref to the given object.
 *
 * Source: https://github.com/tannerlinsley/react-table/blob/aa2835766a7a34f70cd6b579628e4f7e820970ab/src/publicUtils.js#L140 (MIT License)
 */
export function useGetLatest<T extends {}>(obj: T) {
    const ref = React.useRef<T>();
    ref.current = obj;

    return React.useCallback(() => ref.current, []);
}

/**
 * Debounces a given function for a given timespan.
 * @param defaultFn
 * @param defaultWait
 *
 * Source: https://github.com/tannerlinsley/react-table/blob/aa2835766a7a34f70cd6b579628e4f7e820970ab/src/publicUtils.js#L163 (MIT License)
 */
export function useAsyncDebounce<T extends (...args: any[]) => any>(
    defaultFn: T,
    defaultWait: number
): T {
    const debounceRef = React.useRef<{
        promise?: Promise<T>;
        resolve?: (value: T | PromiseLike<T>) => void;
        reject?: (reason?: any) => void;
        timeout?: NodeJS.Timeout;
    }>({});

    // the given args could be props, which change, so we want to always use latest definitions.
    const getDefaultFn = useGetLatest(defaultFn);
    const getDefaultWait = useGetLatest(defaultWait);

    return React.useCallback(
        (async (...args) => {
            // make a promise
            if (!debounceRef.current.promise) {
                debounceRef.current.promise = new Promise((resolve, reject) => {
                    debounceRef.current.resolve = resolve;
                    debounceRef.current.reject = reject;
                });
            }

            if (debounceRef.current.timeout) {
                clearTimeout(debounceRef.current.timeout);
            }

            debounceRef.current.timeout = setTimeout(async () => {
                // Executing a debounced call--don't let it be canceled.
                delete debounceRef.current.timeout;
                try {
                    debounceRef.current.resolve(await getDefaultFn()(...args));
                } catch (err) {
                    debounceRef.current.reject(err);
                } finally {
                    delete debounceRef.current.promise;
                }
            }, getDefaultWait());

            return debounceRef.current.promise;
        }) as T,
        [getDefaultFn, getDefaultWait]
    );
}

export interface ISILTransactionArgs {
    [key: string]: string; // alows object to be indexed
    transaction: string; // required
    csrfToken: string; // required
    arg01?: string;
    arg02?: string;
    arg03?: string;
    arg04?: string;
    arg05?: string;
    arg06?: string;
    arg07?: string;
    arg08?: string;
    arg09?: string;
    arg10?: string;
    arg11?: string;
    arg12?: string;
}
// Taken and typified from post.jsskel :(
// This function is called in the onclick handler of an anchor element that we
// want to change to a POST dynamically.
// Call like example:  return postTransactionUrl({transaction: "becoeinstadmin", csfrToken: "abcd123", arg01: "something"})
export function postTransactionUrl(args: ISILTransactionArgs) {
    try {
        const form = document.createElement("form") as HTMLFormElement;
        form.action = "human.aspx";
        form.method = "post";
        Object.keys(args).forEach(argName => {
            const field = document.createElement("input") as HTMLInputElement;
            field.type = "hidden";
            field.name = argName;
            field.value = args[argName] || "";
            form.appendChild(field);
        });
        document.body.appendChild(form);
        form.submit();
    } catch (e) {
        alert("Post Transaction Failed: " + e);
    }
    return false;
}
